/**
 * ============================================================================
 * Iron Meridian - Country Panel Component
 * ============================================================================
 * 
 * Detailed view of the selected country's stats, buildings, and modifiers.
 * Premium information-dense display with clear visual hierarchy.
 */

import { StatTooltip, TerrainTooltip } from './Tooltip'

// Terrain types with icons and combat modifiers
const TERRAIN_TYPES = {
  plains: { icon: '🌾', name: 'Plains', modifier: 'Balanced' },
  mountains: { icon: '⛰️', name: 'Mountains', modifier: '+25% Defense' },
  forest: { icon: '🌲', name: 'Forest', modifier: '+15% Defense' },
  desert: { icon: '🏜️', name: 'Desert', modifier: '-10% Economy' },
  coastal: { icon: '🌊', name: 'Coastal', modifier: '+20% Trade' },
  arctic: { icon: '❄️', name: 'Arctic', modifier: '+30% Defense, -20% Economy' },
  urban: { icon: '🏙️', name: 'Urban', modifier: '+15% Economy' },
}

// Stance display configuration - use string keys directly
// DIPLOMATIC_STANCES exports objects with { id, label, ... } so we use the id strings
const STANCE_CONFIG = {
  ALLY: { label: 'Ally', icon: '🛡️', color: 'text-green-400', bgColor: 'bg-green-500/20', barColor: 'bg-green-500' },
  FRIENDLY: { label: 'Friendly', icon: '😊', color: 'text-blue-400', bgColor: 'bg-blue-500/20', barColor: 'bg-blue-500' },
  NEUTRAL: { label: 'Neutral', icon: '⚪', color: 'text-gray-400', bgColor: 'bg-gray-500/20', barColor: 'bg-gray-500' },
  WARY: { label: 'Wary', icon: '😐', color: 'text-orange-400', bgColor: 'bg-orange-500/20', barColor: 'bg-orange-500' },
  HOSTILE: { label: 'Hostile', icon: '⚔️', color: 'text-red-400', bgColor: 'bg-red-500/20', barColor: 'bg-red-500' }
}

export default function CountryPanel({ country, isPlayerOwned, isHomeCountry, playerCountry, diplomacyEntry, onOpenDiplomacy }) {
  if (!country) {
    return (
      <div className="p-5">
        <h2 className="text-base font-semibold mb-4 flex items-center gap-2 text-gray-300">
          <span className="w-8 h-8 rounded-lg bg-war-room-700 flex items-center justify-center text-gray-500">📍</span>
          Intelligence Report
        </h2>
        <div className="text-center py-16">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-war-room-700/50 flex items-center justify-center">
            <span className="text-3xl opacity-40">🗺️</span>
          </div>
          <p className="text-gray-500 text-sm">Select a country on the map</p>
          <p className="text-gray-600 text-xs mt-1">to view intelligence details</p>
        </div>
      </div>
    )
  }

  const terrain = TERRAIN_TYPES[country.terrain] || TERRAIN_TYPES.plains

  return (
    <div className="p-5">
      {/* Header with country identity */}
      <div className="flex items-start gap-4 mb-5">
        <div
          className="w-14 h-14 rounded-xl flex items-center justify-center text-xl font-bold shadow-lg ring-2 ring-white/10"
          style={{ backgroundColor: country.color }}
        >
          {country.id?.slice(0, 2) || '??'}
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-bold text-white truncate">{country.name}</h2>
          <div className="flex items-center gap-2 mt-1">
            {isPlayerOwned ? (
              isHomeCountry ? (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-war-room-accent/20 text-war-room-accent rounded-full text-xs font-medium">
                  <span>👑</span> Your Empire
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-500/20 text-green-400 rounded-full text-xs font-medium">
                  <span>🏴</span> Conquered Territory
                </span>
              )
            ) : (
              <>
                <span className="text-sm text-gray-400 capitalize">{country.controller}</span>
                {country.personality && (
                  <span className="px-2 py-0.5 bg-war-room-700/80 text-gray-400 rounded text-xs capitalize">
                    {country.personality}
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Diplomacy Section (for non-player countries) */}
      {!isPlayerOwned && diplomacyEntry && (
        <div className="bg-war-room-800/60 rounded-xl p-4 mb-4 border border-war-room-600/30">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Diplomatic Relations</h3>
          <div className="space-y-3">
            {/* Trust meter - new -100 to +100 scale */}
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-400">Trust Level</span>
                <span className={`font-medium font-mono ${
                  diplomacyEntry.trust >= 0 ? 'text-green-400' : 'text-red-400'
                }`}>
                  {diplomacyEntry.trust >= 0 ? '+' : ''}{diplomacyEntry.trust}
                </span>
              </div>
              <div className="h-2 bg-war-room-700 rounded-full overflow-hidden relative">
                {/* Center line */}
                <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-600 z-10" />
                {/* Trust bar - grows from center */}
                <div 
                  className={`absolute top-0 bottom-0 transition-all duration-300 ${
                    diplomacyEntry.trust >= 60 ? 'bg-green-500' :
                    diplomacyEntry.trust >= 20 ? 'bg-blue-500' :
                    diplomacyEntry.trust >= -19 ? 'bg-gray-500' :
                    diplomacyEntry.trust >= -59 ? 'bg-orange-500' :
                    'bg-red-500'
                  }`}
                  style={{ 
                    left: diplomacyEntry.trust >= 0 ? '50%' : `${50 + (diplomacyEntry.trust / 2)}%`,
                    width: `${Math.abs(diplomacyEntry.trust) / 2}%`
                  }}
                />
              </div>
            </div>
            
            {/* Stance */}
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">Stance</span>
              {(() => {
                const config = STANCE_CONFIG[diplomacyEntry.stance] || STANCE_CONFIG.NEUTRAL
                return (
                  <span className={`px-2 py-0.5 rounded text-xs ${config.bgColor} ${config.color} flex items-center gap-1`}>
                    <span>{config.icon}</span>
                    {config.label}
                  </span>
                )
              })()}
            </div>

            {/* Open diplomacy button */}
            {onOpenDiplomacy && (
              <button
                onClick={onOpenDiplomacy}
                className="w-full mt-2 px-3 py-2 bg-purple-600/30 hover:bg-purple-600/50 text-purple-300 rounded-lg text-sm transition flex items-center justify-center gap-2"
              >
                <span>🌐</span>
                Open Diplomacy
              </button>
            )}
          </div>
        </div>
      )}

      {/* Terrain Section */}
      <TerrainTooltip terrain={country.terrain || 'plains'}>
        <div className="bg-war-room-800/60 rounded-xl p-4 mb-4 border border-war-room-600/30 cursor-help">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-war-room-700 flex items-center justify-center text-2xl">
              {terrain.icon}
            </div>
            <div>
              <div className="font-medium text-gray-200">{terrain.name} Terrain</div>
              <div className="text-xs text-gray-500">{terrain.modifier}</div>
            </div>
          </div>
        </div>
      </TerrainTooltip>

      {/* National Doctrines Section */}
      {country.dominantDoctrines && country.dominantDoctrines.length > 0 && (
        <div className="bg-war-room-800/60 rounded-xl p-4 mb-4 border border-war-room-600/30">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">National Doctrine</h3>
          <div className="space-y-2">
            {country.dominantDoctrines.slice(0, 3).map((doctrine, idx) => (
              <div key={doctrine.type} className="flex items-center gap-2">
                <span className="text-lg">{doctrine.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-300">{doctrine.name}</span>
                    <span className="text-xs font-mono text-gray-400">{doctrine.score}%</span>
                  </div>
                  <div className="h-1.5 bg-war-room-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full transition-all duration-300"
                      style={{ 
                        width: `${doctrine.score}%`,
                        backgroundColor: doctrine.color
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-3 leading-relaxed">
            {country.dominantDoctrines[0]?.description || 'National identity evolves based on behavior and stats'}
          </p>
        </div>
      )}

      {/* Statistics Section */}
      <div className="bg-war-room-800/60 rounded-xl p-4 mb-4 border border-war-room-600/30">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">National Statistics</h3>
        <div className="space-y-4">
          <DetailedStat
            label="Military Power"
            value={country.military}
            max={250}
            color="#ef4444"
            icon="⚔️"
            description="Armed forces strength"
          />
          <DetailedStat
            label="Economy"
            value={country.economy}
            max={250}
            color="#fbbf24"
            icon="💰"
            description="GDP and production"
          />
          <DetailedStat
            label="Technology"
            value={country.technology}
            max={100}
            color="#3b82f6"
            icon="🔬"
            description="Research advancement"
          />
          <DetailedStat
            label="Stability"
            value={country.stability || 80}
            max={100}
            color="#22c55e"
            icon="🏛️"
            description="Civil order"
          />
        </div>
      </div>

      {/* Neighbors Section */}
      {country.neighbors && country.neighbors.length > 0 && (
        <div className="bg-war-room-800/60 rounded-xl p-4 mb-4 border border-war-room-600/30">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Border Nations</h3>
          <div className="flex flex-wrap gap-1.5">
            {country.neighbors.map(neighbor => (
              <span
                key={neighbor}
                className="px-2.5 py-1 bg-war-room-700/80 hover:bg-war-room-600 rounded-lg text-sm text-gray-300 transition-colors cursor-pointer border border-war-room-600/30"
              >
                {neighbor}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Combat Comparison (if not player owned) */}
      {!isPlayerOwned && playerCountry && (
        <div className="bg-war-room-800/60 rounded-xl p-4 border border-war-room-600/30">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Combat Analysis</h3>
          <CombatComparison attacker={{ id: playerCountry }} defender={country} />
        </div>
      )}
    </div>
  )
}

function DetailedStat({ label, value, max, color, icon, description }) {
  const percentage = Math.min((value / max) * 100, 100)
  const tier = percentage >= 75 ? 'Elite' : percentage >= 50 ? 'Strong' : percentage >= 25 ? 'Moderate' : 'Weak'
  const tierColor = percentage >= 75 ? 'text-green-400' : percentage >= 50 ? 'text-blue-400' : percentage >= 25 ? 'text-yellow-400' : 'text-red-400'

  return (
    <div className="group">
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <span className="text-lg">{icon}</span>
          <div>
            <span className="text-sm font-medium text-gray-200">{label}</span>
            <span className="text-xs text-gray-500 ml-2 hidden group-hover:inline">{description}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-medium ${tierColor}`}>{tier}</span>
          <span className="font-bold tabular-nums min-w-[3ch] text-right" style={{ color }}>{value}</span>
        </div>
      </div>
      <div className="h-2 rounded-full bg-war-room-700 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700 ease-out"
          style={{ 
            width: `${percentage}%`, 
            backgroundColor: color,
            boxShadow: `0 0 10px ${color}40`
          }}
        />
      </div>
    </div>
  )
}

function CombatComparison({ attacker, defender }) {
  // Simple comparison - uses actual combat formula
  const attackerStrength = 100 // Would come from actual player data
  const techBonus = 1 + (defender.technology * 0.01)
  const defenderBonus = 1.2
  const defenderStrength = defender.military * techBonus * defenderBonus

  const ratio = attackerStrength / defenderStrength
  const winChance = Math.min(Math.max(ratio * 50, 10), 90)
  
  let assessment, assessmentColor, assessmentIcon
  if (ratio > 1.3) {
    assessment = 'Favorable'
    assessmentColor = 'text-green-400'
    assessmentIcon = '✓'
  } else if (ratio > 0.9) {
    assessment = 'Contested'
    assessmentColor = 'text-yellow-400'
    assessmentIcon = '⚖️'
  } else {
    assessment = 'High Risk'
    assessmentColor = 'text-red-400'
    assessmentIcon = '⚠️'
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-war-room-700/50 rounded-lg p-2.5 text-center">
          <div className="text-xs text-gray-500 mb-1">Your Strength</div>
          <div className="text-lg font-bold text-blue-400">{attackerStrength}</div>
        </div>
        <div className="bg-war-room-700/50 rounded-lg p-2.5 text-center">
          <div className="text-xs text-gray-500 mb-1">Their Defense</div>
          <div className="text-lg font-bold text-red-400">{defenderStrength.toFixed(0)}</div>
        </div>
      </div>
      
      <div className="flex items-center justify-between pt-2 border-t border-war-room-600/50">
        <div>
          <span className="text-xs text-gray-500">Assessment</span>
          <div className={`font-semibold flex items-center gap-1.5 ${assessmentColor}`}>
            <span>{assessmentIcon}</span>
            <span>{assessment}</span>
          </div>
        </div>
        <div className="text-right">
          <span className="text-xs text-gray-500">Est. Success</span>
          <div className={`font-bold text-lg ${assessmentColor}`}>{winChance.toFixed(0)}%</div>
        </div>
      </div>
    </div>
  )
}
